import { ThemeEvents } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';

const ANALYTICS_CUSTOM_EVENT = 'ab:theme-analytics';
const CTA_SELECTOR = 'a.button[href], button.button, [data-analytics-cta]';
const ENGAGED_ACTIVE_TIME_MS = 15000;
const ENGAGED_SCROLL_ACTIVE_TIME_MS = 5000;
const ENGAGED_SCROLL_PERCENT = 50;
const CTA_IGNORE_SELECTOR = [
  '.add-to-cart-button',
  '.ab-inline-bundle-picker__button',
  '.ab-tiered-bundle-picker__button',
  '.ab-tiered-bundle-picker__sticky-button',
  '.cart-discount__button',
  '.cart-discount__pill-remove',
  '.quantity-selector button',
  '[aria-controls]',
].join(',');
const RESERVED_ATTRIBUTION_PARAMETER_RENAMES = {
  source: 'ab_source',
  medium: 'ab_medium',
  campaign: 'ab_campaign',
  term: 'ab_term',
  content: 'ab_content',
  campaign_id: 'ab_campaign_id',
  campaign_name: 'ab_campaign_name',
  source_platform: 'ab_source_platform',
};

const pendingQuantityClicks = new WeakMap();
const pendingQuantitySelectors = new WeakMap();
let engagedPageViewPublished = false;
let activeVisibleMs = 0;
let lastEngagementTick = window.performance?.now ? window.performance.now() : Date.now();
let maxScrollPercent = 0;

function cleanString(value, maxLength = 160) {
  if (value === undefined || value === null) return undefined;

  const text = String(value)
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[redacted]')
    .replace(/\+?\d[\d\s().-]{7,}\d/g, '[redacted]')
    .replace(/\s+/g, ' ')
    .trim();

  return text ? text.slice(0, maxLength) : undefined;
}

function cleanIdentifier(value, maxLength = 120) {
  if (value === undefined || value === null) return undefined;

  const text = String(value).replace(/\s+/g, '').trim();
  return text ? text.slice(0, maxLength) : undefined;
}

function cleanUrl(value) {
  if (!value) return undefined;

  try {
    const url = new URL(value, window.location.origin);
    return `${url.origin}${url.pathname}`;
  } catch (_error) {
    return cleanString(value, 240);
  }
}

function shouldNormalizeUrlParameter(key) {
  return key === 'page_location' || /(^|_)(url|href)$/.test(key);
}

function sanitizePayload(payload) {
  return Object.entries(payload || {}).reduce((safe, [rawKey, value]) => {
    const key = RESERVED_ATTRIBUTION_PARAMETER_RENAMES[rawKey] || rawKey;
    if (value === undefined || value === null || value === '') return safe;

    if (/email|phone|address|name/i.test(key) && !/^item_name$/.test(key)) {
      return safe;
    }

    if (Array.isArray(value)) {
      safe[key] = value.map((item) => (typeof item === 'object' ? sanitizePayload(item) : cleanString(item))).filter(Boolean);
      return safe;
    }

    if (typeof value === 'object') {
      safe[key] = sanitizePayload(value);
      return safe;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      if (key !== rawKey) {
        return safe;
      }

      safe[key] = value;
      return safe;
    }

    if (/^(product_id|variant_id|source_id)$/.test(key)) {
      safe[key] = cleanIdentifier(value);
      return safe;
    }

    safe[key] = shouldNormalizeUrlParameter(key) ? cleanUrl(value) : cleanString(value);
    return safe;
  }, {});
}

function pageContext() {
  return {
    page_path: window.location.pathname,
    page_template: window.Theme?.template?.name,
  };
}

function numberValue(value) {
  if (value === undefined || value === null || String(value).trim() === '') return undefined;

  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function productContextFromElement(element) {
  const productRoot =
    element.closest('[data-product-id]') ||
    document.querySelector('product-form-component[data-product-id], sticky-add-to-cart[data-product-id]');
  const variantRoot =
    element.closest('[data-current-variant-id]') || document.querySelector('sticky-add-to-cart[data-current-variant-id]');
  const variantInput =
    element.closest('product-form-component')?.querySelector('input[name="id"]') ||
    document.querySelector('product-form-component input[name="id"]');
  const variantSelector = element.closest('[data-variant-id]');

  return {
    product_id: productRoot?.getAttribute('data-product-id'),
    variant_id:
      variantSelector?.getAttribute('data-variant-id') ||
      variantRoot?.getAttribute('data-current-variant-id') ||
      variantInput?.value,
  };
}

function surfaceFromElement(element) {
  if (element.closest('sticky-add-to-cart')) return 'sticky_add_to_cart';
  if (element.closest('cart-quantity-selector-component, .cart-items, cart-drawer-component')) return 'cart';
  if (element.closest('.quick-add-modal')) return 'quick_add';
  if (element.closest('.product-media-gallery, [data-testid="product-information-media"]')) return 'product_gallery';
  if (element.closest('product-form-component')) return 'product_form';
  return 'storefront';
}

function quantityInputFromContainer(container) {
  return container?.querySelector('input[type="number"], input[name="quantity"], .sticky-add-to-cart__quantity-input');
}

function quantityValuesFromContainer(container) {
  const quantityInput = quantityInputFromContainer(container);
  const value = numberValue(quantityInput?.value);
  const min = numberValue(quantityInput?.min) || 1;
  const max = numberValue(quantityInput?.max);
  const step = numberValue(quantityInput?.step) || 1;

  return { value, min, max, step };
}

function quantityActionFromButton(button) {
  if (button.matches('.quantity-plus, .sticky-add-to-cart__quantity-button[name="plus"], [name="plus"]')) return 'increase';
  if (button.matches('.quantity-minus, .sticky-add-to-cart__quantity-button[name="minus"], [name="minus"]')) return 'decrease';
  return undefined;
}

function quantityButtonFromTarget(target) {
  const button = target.closest('.quantity-selector button, .sticky-add-to-cart__quantity-button');
  return button instanceof HTMLButtonElement ? button : undefined;
}

function quantityContainerFromButton(button) {
  return button.closest('.quantity-selector, .sticky-add-to-cart__quantity');
}

function rememberQuantityPointerDown(target) {
  const button = quantityButtonFromTarget(target);
  if (!button || button.disabled) return;

  const quantityContainer = quantityContainerFromButton(button);
  const action = quantityActionFromButton(button);
  if (!quantityContainer || !action) return;

  const values = quantityValuesFromContainer(quantityContainer);
  const state = {
    action,
    button,
    previousQuantity: values.value,
    published: false,
  };
  pendingQuantityClicks.set(button, state);
  pendingQuantitySelectors.set(quantityContainer, state);
}

function mediaIndexFromElement(element) {
  const clickAction = element.getAttribute('on:click') || '';
  const match = clickAction.match(/\/(?:zoom|open)\/(\d+)/);
  return match ? numberValue(match[1]) : undefined;
}

function mediaTypeFromElement(element) {
  const mediaContainer = element.closest('.product-media-container');
  if (!mediaContainer) return undefined;

  const mediaTypeClass = Array.from(mediaContainer.classList).find((className) =>
    className.startsWith('product-media-container--')
  );
  return mediaTypeClass?.replace('product-media-container--', '');
}

function publishThemeEvent(eventName, payload = {}) {
  if (!eventName || !window.Shopify?.analytics?.publish) return;

  window.Shopify.analytics.publish(eventName, {
    ...pageContext(),
    ...sanitizePayload(payload),
  });
}

function currentScrollPercent() {
  const documentElement = document.documentElement;
  const body = document.body;
  const scrollTop = window.scrollY || documentElement.scrollTop || body?.scrollTop || 0;
  const scrollHeight = Math.max(
    documentElement.scrollHeight,
    body?.scrollHeight || 0,
    documentElement.offsetHeight,
    body?.offsetHeight || 0,
    documentElement.clientHeight
  );
  const scrollableHeight = Math.max(scrollHeight - window.innerHeight, 0);

  if (!scrollableHeight) return 100;

  return Math.max(0, Math.min(100, Math.round((scrollTop / scrollableHeight) * 100)));
}

function updateMaxScrollPercent() {
  maxScrollPercent = Math.max(maxScrollPercent, currentScrollPercent());
}

function publishEngagedPageView(engagementReason) {
  if (engagedPageViewPublished) return;

  engagedPageViewPublished = true;
  publishThemeEvent('ab_engaged_page_view', {
    component: 'engagement_tracker',
    surface: 'storefront',
    engagement_reason: engagementReason,
    active_time_seconds: Math.round(activeVisibleMs / 1000),
    scroll_percent: maxScrollPercent,
  });
}

function evaluateEngagedPageView() {
  if (engagedPageViewPublished) return;

  if (activeVisibleMs >= ENGAGED_ACTIVE_TIME_MS) {
    publishEngagedPageView('active_time_15s');
    return;
  }

  if (maxScrollPercent >= ENGAGED_SCROLL_PERCENT && activeVisibleMs >= ENGAGED_SCROLL_ACTIVE_TIME_MS) {
    publishEngagedPageView('scroll_50_and_active_time_5s');
  }
}

function startEngagementTracker() {
  updateMaxScrollPercent();

  window.setInterval(() => {
    const now = window.performance?.now ? window.performance.now() : Date.now();
    if (document.visibilityState === 'visible') {
      activeVisibleMs += Math.max(0, now - lastEngagementTick);
      evaluateEngagedPageView();
    }
    lastEngagementTick = now;
  }, 1000);

  window.addEventListener(
    'scroll',
    () => {
      window.requestAnimationFrame(() => {
        updateMaxScrollPercent();
        evaluateEngagedPageView();
      });
    },
    { passive: true }
  );

  window.addEventListener(
    'resize',
    () => {
      window.requestAnimationFrame(updateMaxScrollPercent);
    },
    { passive: true }
  );

  document.addEventListener('visibilitychange', () => {
    lastEngagementTick = window.performance?.now ? window.performance.now() : Date.now();
  });
}

document.addEventListener(ANALYTICS_CUSTOM_EVENT, (event) => {
  const { eventName, data } = event.detail || {};
  publishThemeEvent(eventName, data);
});

startEngagementTracker();

function handleProductImageZoomClick(target) {
  const zoomTarget = target.closest(
    '.product-media-container__zoom-button, [on\\:click*="/zoom/"], [on\\:click*="/open/"]'
  );
  if (!(zoomTarget instanceof HTMLElement)) return false;

  const mediaContainer = zoomTarget.closest('.product-media-container');
  if (!mediaContainer) return false;

  publishThemeEvent('ab_product_image_zoomed', {
    ...productContextFromElement(zoomTarget),
    component: 'product_media_gallery',
    surface: 'product_gallery',
    action: 'open',
    media_type: mediaTypeFromElement(zoomTarget),
    media_index: mediaIndexFromElement(zoomTarget),
  });

  return true;
}

function publishQuantityChangedEvent(button, action, quantity, previousQuantity) {
  publishThemeEvent('ab_quantity_changed', {
    ...productContextFromElement(button),
    component: 'quantity_selector',
    surface: surfaceFromElement(button),
    action,
    quantity,
    previous_quantity: previousQuantity,
  });
}

function publishQuantityState(state, quantity) {
  if (state.published || quantity === undefined || quantity === state.previousQuantity) return;

  state.published = true;
  publishQuantityChangedEvent(state.button, state.action, quantity, state.previousQuantity);
}

function scheduleQuantityChangePublish(state) {
  const { button, previousQuantity } = state;
  if (previousQuantity === undefined) return;

  const publishIfChanged = () => {
    if (state.published) return;

    const quantityContainer = quantityContainerFromButton(button);
    if (!quantityContainer) return;

    const values = quantityValuesFromContainer(quantityContainer);
    publishQuantityState(state, values.value);
  };

  [0, 50, 150, 300, 600, 1000].forEach((delay) => window.setTimeout(publishIfChanged, delay));
}

function handleQuantityButtonClick(button) {
  const quantityContainer = quantityContainerFromButton(button);
  if (!quantityContainer || button.disabled) return;

  const action = quantityActionFromButton(button);
  if (!action) return;

  const pendingClick = pendingQuantityClicks.get(button);
  pendingQuantityClicks.delete(button);

  const values = quantityValuesFromContainer(quantityContainer);
  const state =
    pendingClick?.action === action
      ? pendingClick
      : {
          action,
          button,
          previousQuantity: values.value,
          published: false,
        };

  state.button = button;
  state.action = action;
  state.previousQuantity = state.previousQuantity ?? values.value;
  pendingQuantitySelectors.set(quantityContainer, state);
  scheduleQuantityChangePublish(state);
}

document.addEventListener(DialogOpenEvent.eventName, (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (!target.matches('cart-drawer-component') && !target.querySelector('.cart-drawer')) return;

  publishThemeEvent('ab_cart_drawer_opened', {
    component: 'cart_drawer',
  });
});

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  const variant = event.detail?.resource;
  const productId = event.detail?.data?.productId;
  if (!variant?.id || !productId) return;

  publishThemeEvent('ab_variant_selected', {
    product_id: productId,
    variant_id: variant.id,
    variant_available: variant.available !== false,
    source_id: event.detail?.sourceId,
  });
});

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const quantityContainer = target.closest('.quantity-selector, .sticky-add-to-cart__quantity');
  if (!quantityContainer) return;

  const state = pendingQuantitySelectors.get(quantityContainer);
  if (!state) return;

  pendingQuantitySelectors.delete(quantityContainer);
  publishQuantityState(state, numberValue(event.detail?.quantity));
});

document.addEventListener(
  'pointerdown',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    rememberQuantityPointerDown(target);
  },
  { capture: true }
);

document.addEventListener(
  'mousedown',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    rememberQuantityPointerDown(target);
  },
  { capture: true }
);

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (handleProductImageZoomClick(target)) return;

    const quantityButton = quantityButtonFromTarget(target);
    if (quantityButton) {
      handleQuantityButtonClick(quantityButton);
      return;
    }

    const cta = target.closest(CTA_SELECTOR);
    if (!(cta instanceof HTMLElement) || cta.matches(CTA_IGNORE_SELECTOR) || cta.closest(CTA_IGNORE_SELECTOR)) return;

    if (cta.matches('.cart__checkout-button')) {
      publishThemeEvent('ab_checkout_button_clicked', {
        cta_text: cta.textContent,
        cta_location: 'cart',
      });
      return;
    }

    publishThemeEvent('ab_cta_clicked', {
      cta_text: cta.textContent,
      cta_href: cta instanceof HTMLAnchorElement ? cta.href : undefined,
      cta_location: cta.closest('header, footer, main, section')?.tagName?.toLowerCase(),
      cta_class: cta.className,
    });
  },
  { capture: true }
);
